import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Radio, RadioGroupField, ToolBar } from '@1c/ui';
import { useNavigate } from 'src/utils/useNavigate';
import { FinalField, FinalForm } from 'src/components/FinalForm';
import PhoneInput from 'src/components/PhoneInput';
import Button from 'src/components/Button';
import Text from 'src/components/Text';
import {
  postAuthorizationStartAPI,
  getAuthorizationInfoAPI,
} from 'src/serverApi/authorization';
import routes from 'src/constants/routes';
import styles from './styles.module.scss';

type Props = {
  onSubmit: (authorizationInfo: any, recoveryType: any) => void;
};

const StartRecoveryForm: React.FC<Props> = ({ onSubmit }) => {
  const navigate = useNavigate();
  const [isEmptyPhone, setIsEmptyPhone] = useState(true);
  const [authorizationError, setAuthorizationError] = useState('');
  
  const setPhone = async ({ phone, recoveryType }: { phone: any, recoveryType: any }) => {
    if (!phone || phone === '+7') {
      return;
    }
    
    try {
      setAuthorizationError('');
      await postAuthorizationStartAPI(phone);
      const authorizationInfo = await getAuthorizationInfoAPI();
      
      if (!authorizationInfo?.hasPassword) {
        setAuthorizationError('Пользователь с таким номером телефона не найден');
      } else if (!authorizationInfo?.confirmedEmailMask && recoveryType === 'email') {
        setAuthorizationError('У данного пользователя не подтвержден адрес почты');
      } else {
        onSubmit(authorizationInfo, recoveryType);
      }
    } catch (error: any) {
      if (error.type === 'BAD_REQUEST') {
        // это валидационная ошибка, при этом у нее такой тип, потому что разработчик бэкенда решил,
        // что других ошибок в этом запросе быть не может, поэтому и так нормально
        setAuthorizationError('Неверный формат, номер должен содержать только цифры и иметь длину не меньше 7 символов');
        return;
      }
      if (error.type === 'PHONE_PROCESSING_EXCEPTION') {
        setAuthorizationError('Не удалось отправить СМС на указанный номер. Проверьте правильность введенного номера телефона или повторите попытку позже');
        return;
      }
      toast.error('Произошла ошибка. Попробуйте позже.');
    }
  };
  
  const handleBack = () => {
    navigate(routes.authorization);
  }

  const handleSubmit = (values: any) => {
    setPhone(values);
  };

  return (
    <FinalForm
      onSubmit={handleSubmit}
      pristine
      initialValues={{ phone: null, recoveryType: 'phone' }}
    >
      {({ submitting }: { submitting: any }) => (
        <div className={styles['form']}>
          <FinalField name="phone">
            {({ value, onChange }: { value: any, onChange: any }) => {
              setIsEmptyPhone(!value || value === '+7')
              return (
                <PhoneInput
                  value={value}
                  onChange={(params) => {
                    setAuthorizationError('');
                    onChange(params);
                  }}
                  label="Номер телефона"
                  error={authorizationError}
                />
              )
            }}
          </FinalField>
          <FinalField name="recoveryType">
            {(props: any) => (
              <div className={styles['radio']}>
                <Text type="p" muted>Выберите метод восстановления</Text>
                <RadioGroupField {...props} inline={false}>
                  <Radio value="phone" key="phone">
                    Отправить код с помощью СМС
                  </Radio>
                  <Radio value="email" key='email'>
                    Отправить код на e-mail
                  </Radio>
                </RadioGroupField>
              </div>
            )}
          </FinalField>
          <ToolBar align="center" className={styles['buttons']}>
            <Button variant="text" disabled={submitting} onClick={handleBack}>
              Назад
            </Button>
            <Button disabled={submitting || isEmptyPhone} type="submit">
              Продолжить
            </Button>
          </ToolBar>
        </div>
      )}
    </FinalForm>
  );
};

export default StartRecoveryForm;
